import React, { useRef, useCallback, useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Divider,
  Typography,
  Tooltip,
  Paper
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Undo,
  Redo,
  FormatClear
} from '@mui/icons-material';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  minHeight?: number;
  sx?: any;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Start typing...",
  disabled = false,
  maxLength = 1500,
  minHeight = 120,
  sx = {}
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [characterCount, setCharacterCount] = useState(0);
  const [isFocused, setIsFocused] = useState(false);

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
      updateCharacterCount();
    }
  }, [value]);

  const updateCharacterCount = useCallback(() => {
    if (editorRef.current) {
      const text = editorRef.current.innerText || '';
      setCharacterCount(text.length);
    }
  }, []);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      const text = editorRef.current.innerText || '';
      
      // Check character limit
      if (text.length > maxLength) {
        // Prevent further input if limit exceeded
        return;
      }
      
      onChange(content);
      updateCharacterCount();
    }
  }, [onChange, maxLength, updateCharacterCount]);

  const executeCommand = useCallback((command: string, value?: string) => {
    if (disabled) return;
    
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  }, [disabled, handleInput]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return;

    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          executeCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          executeCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          executeCommand('underline');
          break;
        case 'z':
          e.preventDefault();
          if (e.shiftKey) {
            executeCommand('redo');
          } else {
            executeCommand('undo');
          }
          break;
      }
    }

    // Handle character limit
    const text = editorRef.current?.innerText || '';
    if (text.length >= maxLength && 
        !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key) &&
        !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }
  }, [disabled, executeCommand, maxLength]);

  const getCharacterCountColor = () => {
    const percentage = (characterCount / maxLength) * 100;
    if (percentage > 90) return '#f44336';
    if (percentage > 75) return '#ff9800';
    return '#535862';
  };

  const toolbarButtons = [
    { command: 'bold', icon: FormatBold, tooltip: 'Bold (Ctrl+B)' },
    { command: 'italic', icon: FormatItalic, tooltip: 'Italic (Ctrl+I)' },
    { command: 'underline', icon: FormatUnderlined, tooltip: 'Underline (Ctrl+U)' },
    { divider: true },
    { command: 'insertUnorderedList', icon: FormatListBulleted, tooltip: 'Bullet List' },
    { command: 'insertOrderedList', icon: FormatListNumbered, tooltip: 'Numbered List' },
    { divider: true },
    { command: 'undo', icon: Undo, tooltip: 'Undo (Ctrl+Z)' },
    { command: 'redo', icon: Redo, tooltip: 'Redo (Ctrl+Shift+Z)' },
    { command: 'removeFormat', icon: FormatClear, tooltip: 'Clear Formatting' }
  ];

  return (
    <Box sx={{ width: '100%', ...sx }}>
      {/* Toolbar */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          borderRadius: '8px 8px 0 0',
          border: '1px solid #E0E3E7',
          borderBottom: 'none',
          backgroundColor: '#FAFBFC',
          gap: 0.5,
          flexWrap: 'wrap'
        }}
      >
        {toolbarButtons.map((button, index) => {
          if (button.divider) {
            return (
              <Divider
                key={`divider-${index}`}
                orientation="vertical"
                flexItem
                sx={{ margin: '0 4px', height: '24px' }}
              />
            );
          }

          const IconComponent = button.icon!;
          return (
            <Tooltip key={button.command} title={button.tooltip} arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => executeCommand(button.command!)}
                  sx={{
                    padding: '6px',
                    borderRadius: '4px',
                    color: disabled ? '#D1D5DB' : '#535862',
                    '&:hover': {
                      backgroundColor: disabled ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                      color: disabled ? '#D1D5DB' : '#181D27'
                    },
                    '&:active': {
                      backgroundColor: disabled ? 'transparent' : 'rgba(0, 0, 0, 0.08)'
                    }
                  }}
                >
                  <IconComponent sx={{ fontSize: '18px' }} />
                </IconButton>
              </span>
            </Tooltip>
          );
        })}
      </Paper>

      {/* Editor */}
      <Box
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        sx={{
          minHeight: `${minHeight}px`,
          padding: '16px',
          border: '1px solid #E0E3E7',
          borderRadius: '0 0 8px 8px',
          fontSize: '14px',
          lineHeight: 1.5,
          color: disabled ? '#9CA3AF' : '#181D27',
          backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
          outline: 'none',
          cursor: disabled ? 'not-allowed' : 'text',
          transition: 'border-color 0.2s ease-in-out',
          borderColor: isFocused ? '#3B82F6' : '#E0E3E7',
          '&:hover': {
            borderColor: disabled ? '#E0E3E7' : (isFocused ? '#3B82F6' : '#9CA3AF')
          },
          '&:empty::before': {
            content: `"${placeholder}"`,
            color: '#9CA3AF',
            fontStyle: 'italic'
          },
          // Rich text styles
          '& p': {
            margin: '0 0 12px 0',
            '&:last-child': {
              marginBottom: 0
            }
          },
          '& ul, & ol': {
            margin: '12px 0',
            paddingLeft: '24px'
          },
          '& li': {
            marginBottom: '4px'
          },
          '& strong, & b': {
            fontWeight: 600
          },
          '& em, & i': {
            fontStyle: 'italic'
          },
          '& u': {
            textDecoration: 'underline'
          },
          // Responsive adjustments
          '@media (max-width: 600px)': {
            padding: '12px',
            fontSize: '16px', // Prevent zoom on iOS
            minHeight: `${Math.max(minHeight - 20, 80)}px`
          }
        }}
      />

      {/* Character Count */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginTop: 1,
        '@media (max-width: 600px)': {
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.5
        }
      }}>
        <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>
          Use toolbar buttons or keyboard shortcuts for formatting
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            color: getCharacterCountColor(),
            fontWeight: characterCount > maxLength * 0.9 ? 600 : 400
          }}
        >
          {maxLength - characterCount} characters left
        </Typography>
      </Box>
    </Box>
  );
};

export default RichTextEditor;